/**
 * Enemy roster classification + pure spawn/placement helpers shared by the
 * server (behavior + spawn) and client (geometry + placement reticle). Stats
 * stay server-side (drones.ts KIND_STATS) and looks stay client-side
 * (drones.ts KIND_LOOK); this module holds only the cross-cutting facts and
 * deterministic math both sides must agree on.
 */
import { DRONE_KINDS, type DroneKind } from "./protocol";
import * as pmath from "./pmath.js";

type V3 = [number, number, number];

/** How a kind moves and attacks. flying: free 3D + ranged; grounded:
 *  terrain-hugging + ranged; melee: terrain-hugging + contact damage. */
export type DroneClass = "flying" | "grounded" | "melee";

/** Single source of truth for each kind's class (server behavior + client mesh). */
export const DRONE_CLASS: Record<DroneKind, DroneClass> = {
  gunner: "flying",
  scout: "flying",
  heavy: "flying",
  swarmer: "flying",
  sniper: "flying",
  skirmisher: "grounded",
  trooper: "grounded",
  bastion: "grounded",
  biter: "melee",
  mauler: "melee",
  brute: "melee",
  // Humanoid "PLAYER" enemies — same movement classes, but rendered as avatars.
  player_ground: "grounded",
  player_flyer: "flying",
  player_brawler: "melee",
  // Cache minibosses: same two movement classes as the humanoids they extend —
  // the warden brawls, the revenant fights at range and closes when hurt. Every
  // stagger / parry / launcher interaction keys off these (grounded ⇒ launchable
  // and staggerable), so a miniboss is a legal juggle target like any other body.
  warden: "melee",
  revenant: "grounded",
  // The blade DUELIST (cache rank-and-file). Melee like the brawler it extends, so
  // every stagger / parry / launcher / juggle interaction holds unchanged.
  duelist: "melee",
  // The RAIDER (cache rank-and-file, ranged). Grounded like the `player_ground`
  // operative it extends and the revenant whose distance ladder it wears — so it is
  // launchable, staggerable and riposte-able exactly like the duelist beside it.
  raider: "grounded",
};

/** How the CLIENT renders a kind: "mesh" = procedural drone geometry, "avatar" =
 *  a full character model driven by the same state stream (humanoid enemies).
 *  Single source of truth so adding humanoid kinds later is one entry. */
export const DRONE_RENDER: Record<DroneKind, "mesh" | "avatar"> = {
  gunner: "mesh", scout: "mesh", heavy: "mesh", swarmer: "mesh", sniper: "mesh",
  skirmisher: "mesh", trooper: "mesh", bastion: "mesh",
  biter: "mesh", mauler: "mesh", brute: "mesh",
  player_ground: "avatar", player_flyer: "avatar", player_brawler: "avatar",
  warden: "avatar", revenant: "avatar",
  duelist: "avatar", raider: "avatar",
};

/** True when a kind renders as a humanoid avatar (vs a procedural mesh). */
export function isHumanoid(kind: DroneKind): boolean {
  return DRONE_RENDER[kind] === "avatar";
}

/** Held weapon item id for a humanoid kind (null = unarmed fists). Drives the
 *  client avatar attachment + aim; the server reads it only to choose ranged
 *  vs. melee behavior via the archetype. */
export const HUMANOID_WEAPON: Record<string, string | null> = {
  player_ground: "arm-cannon", // same construct the player carries (class "main" → ranged)
  player_flyer: "arm-cannon",
  player_brawler: null, // fists / Kickbox combat
  warden: null, // a brawler miniboss — fists, and the reach to use them
  revenant: "arm-cannon", // trades at range until phase 2, then closes
  // The duelist fences with the PLAYER'S OWN construct — same blade, same sd_ armed
  // footwork (avatarDriver gates setSwordFocus on a melee weapon), and its relayed
  // strikes are the player's own sword_light_* clips (drones.ts `melee()`).
  duelist: "light-blade",
  // The raider carries the player's own arm cannon, like `player_ground` and the
  // revenant it is the rank-and-file version of — so a cache garrison reads at a
  // glance as ARMED PEOPLE: blades on the duelists, cannons on the raiders.
  raider: "arm-cannon",
};

/** Spawn-request bounds, shared by the client clamp and server validation. */
export const SPAWN_LIMITS = {
  countMin: 1,
  countMax: 16,
  spreadMin: 2,
  spreadMax: 40,
  /** Max distance (m) a god placement point may sit from the requester. */
  leash: 600,
} as const;

/** Default scatter radius when a request omits spread (between spreadMin and spreadMax). */
export const DEFAULT_SPREAD = 8;

function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

function isFiniteTriple(v: unknown): v is V3 {
  return (
    Array.isArray(v) &&
    v.length === 3 &&
    v.every((n) => typeof n === "number" && Number.isFinite(n))
  );
}

/** Two orthonormal vectors spanning the plane tangent to the radial at `p`. */
function tangentBasis(p: V3): [V3, V3] {
  const l = pmath.hypot(p[0], p[1], p[2]) || 1;
  const n: V3 = [p[0] / l, p[1] / l, p[2] / l];
  const seed: V3 = Math.abs(n[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  let t1: V3 = [
    seed[1] * n[2] - seed[2] * n[1],
    seed[2] * n[0] - seed[0] * n[2],
    seed[0] * n[1] - seed[1] * n[0],
  ];
  const t1l = pmath.hypot(t1[0], t1[1], t1[2]) || 1;
  t1 = [t1[0] / t1l, t1[1] / t1l, t1[2] / t1l];
  const t2: V3 = [
    n[1] * t1[2] - n[2] * t1[1],
    n[2] * t1[0] - n[0] * t1[2],
    n[0] * t1[1] - n[1] * t1[0],
  ];
  return [t1, t2];
}

/** Remove the radial component of `v` at position `p` so the result lies in the
 *  surface-tangent plane (grounded/melee units follow terrain, never climb). */
export function projectToTangent(p: V3, v: V3): V3 {
  const l = pmath.hypot(p[0], p[1], p[2]) || 1;
  const nx = p[0] / l;
  const ny = p[1] / l;
  const nz = p[2] / l;
  const radial = v[0] * nx + v[1] * ny + v[2] * nz;
  return [v[0] - nx * radial, v[1] - ny * radial, v[2] - nz * radial];
}

/** Deterministic scatter of `n` points around `center`, each within `spread`
 *  on the tangent plane (uniform over the disc). Callers re-seat each point to
 *  terrain afterward; this only offsets laterally. */
export function scatterPositions(center: V3, n: number, spread: number, rng: () => number): V3[] {
  const [t1, t2] = tangentBasis(center);
  const out: V3[] = [];
  for (let i = 0; i < n; i++) {
    const r = spread * Math.sqrt(rng()); // sqrt → uniform over the disc
    const a = rng() * Math.PI * 2;
    const ox = pmath.cos(a) * r;
    const oy = pmath.sin(a) * r;
    out.push([
      center[0] + t1[0] * ox + t2[0] * oy,
      center[1] + t1[1] * ox + t2[1] * oy,
      center[2] + t1[2] * ox + t2[2] * oy,
    ]);
  }
  return out;
}

/** Clamp/validate a raw (client-supplied) spawn request. Returns null when the
 *  placement point is missing or non-finite (the only hard reject here; the
 *  server also enforces the distance leash + god gate). */
export function clampSpawnArgs(raw: {
  kind?: unknown;
  n?: unknown;
  at?: unknown;
  spread?: unknown;
  calm?: unknown;
}): { kind: DroneKind; n: number; at: V3; spread: number; calm: boolean } | null {
  if (!isFiniteTriple(raw.at)) return null;
  const kind =
    typeof raw.kind === "string" && (DRONE_KINDS as readonly string[]).includes(raw.kind)
      ? (raw.kind as DroneKind)
      : "gunner";
  const n =
    typeof raw.n === "number" && Number.isFinite(raw.n)
      ? clamp(Math.round(raw.n), SPAWN_LIMITS.countMin, SPAWN_LIMITS.countMax)
      : 1;
  const spread =
    typeof raw.spread === "number" && Number.isFinite(raw.spread)
      ? clamp(raw.spread, SPAWN_LIMITS.spreadMin, SPAWN_LIMITS.spreadMax)
      : DEFAULT_SPREAD;
  // CALM stance (2026-08-17): spawn with a tight guard leash so the group
  // lives its idle life until genuinely provoked — the observation mode for
  // idle behaviors/interactions the default hostile spawn never shows (a spawn
  // 50 m out sits inside every kind's aggro range).
  return { kind, n, at: raw.at, spread, calm: raw.calm === true };
}
